import React, { useEffect, useState } from "react";
import { useSelector, useDispatch } from "react-redux";
import { useParams } from "react-router-dom";
import { Helmet } from "react-helmet";
import { useFlags } from "launchdarkly-react-client-sdk";

import { usePermissions } from "components/Hook";
import TopHeader from "components/common/TopHeader";
import PageLoader from "components/common/PageLoader";
import TimeoffTab from "components/Screens/Workforce/UserDetailsV2/Timeoff";
import ProfileTab from "components/Screens/Workforce/UserDetailsV2/ProfileTab";
import DocumentsTab from "components/Widgets/BambooHRTabs/Documents";
import ExperienceTab from "components/Widgets/BambooHRTabs/Experience";
import EducationTab from "components/Widgets/BambooHRTabs/Education";
import EditSalary from "components/Screens/Workforce/UserDetailsV2/EditSalary";
import PayrollHistoryTab from "components/Screens/Workforce/UserDetailsV2/PayrollHistoryTab";
import ProfileStrip from "components/common/ProfileStrip";
import TimeoffNotAvailable from "components/Screens/Workforce/UserDetailsV2/Timeoff/TimeoffNotavailable";
import TimesheetTab from "components/Screens/Workforce/UserDetailsV2/TimesheetTab";
import {
  getFieldValue,
  getMonthFromDate,
  getValue,
  isEmptyObject,
  isValidResponse,
} from "components/Screens/CommonUtils";
import ToolBar from "components/common/PageToolbar";
import { WORKFORCE_PROFILE_TABS } from "constants";

import { employeeSalaryDetails } from "store/actions/Workforce/EmployeeDetils/SalaryDetails";
import { employeeSalaryInfo } from "store/actions/Workforce/EmployeeDetils/SalaryInfo";
import { GetEmployeeDetails } from "store/actions/GetEmployeeDetails";

import { getTimeOffAllowedCountries } from "selectors/platformConfig";

import "./user-details.css";

function UserDetails() {
  const dispatch = useDispatch();
  const params = useParams();
  const { anniversaryBasedTimeOff, platformHeader, clockinCustomerExperience } = useFlags();

  const [currentTab, setCurrentTab] = useState(WORKFORCE_PROFILE_TABS[0]?.title);

  const { loggedInUser, loggedInUserType, canAccessFinancials } = usePermissions();
  const showFinancials = canAccessFinancials();

  const userAdditionalInfo = useSelector((state) => state.UserAdditionalInfo.userAdditionalInfo);
  const { clockinExperienceEnabled } = userAdditionalInfo || {};
  const salaryInfo = useSelector((state) => state.SalaryInfo.employeeSalaryInfo);
  const getEmployeeDetails = useSelector((state) => state.GetEmployeeDetails.getEmployeeDetails);
  const loadingEmployeeSalaryInfo = useSelector((state) => state.SalaryInfo.loadingEmployeeSalaryInfo);
  const loadingGetEmployeeDetails = useSelector((state) => state.GetEmployeeDetails.loadingGET_EMPLOYEE_Details);
  const timeOffAllowedCountries = useSelector((state) => getTimeOffAllowedCountries(state, anniversaryBasedTimeOff));

  const {
    firstName = "",
    lastName = "",
    employeeId = "",
    costToCompany = 0,
    country = "",
    timeoffFlag = false,
    incrementDetails = {},
    timeoffCountry,
  } = getEmployeeDetails?.payload || {};

  const { bonus = 0, incrementAmount = 0 } = incrementDetails;

  useEffect(() => {
    if (isEmptyObject(loggedInUser) && isValidResponse(getEmployeeDetails)) {
      if (loggedInUserType === "customer" && !showFinancials) {
        return;
      }
      const customerId = loggedInUser?.customerId;
      const salaryInfoRequest = {
        customer: `customer/${customerId}/employee/${employeeId}/salaryInfo`,
        edge: `employee/${employeeId}/salary`,
      };

      const salaryDetailsRequest = {
        customer: `customer/${customerId}/employee/${employeeId}/payroll`,
        edge: `employee/${employeeId}/payroll`,
      };

      dispatch(
        employeeSalaryDetails({
          request: salaryDetailsRequest[loggedInUserType],
        })
      );

      dispatch(
        employeeSalaryInfo({
          request: salaryInfoRequest[loggedInUserType],
        })
      );
    }
  }, [getEmployeeDetails, employeeId, loggedInUser, loggedInUserType, showFinancials, dispatch]);

  useEffect(() => {
    if (isEmptyObject(loggedInUser) && params?.id) {
      const request = {
        customer: `customer/${loggedInUser?.customerId}/employee/${params?.id}`,
        edge: `employee/${params?.id}`,
      };

      dispatch(
        GetEmployeeDetails({
          request: request[loggedInUserType],
        })
      );
    }
  }, [loggedInUser, params?.id, loggedInUserType, dispatch]);

  useEffect(() => {
    const tabKey = decodeURIComponent(window?.location.hash.replace(/#/g, "").trim()).toLowerCase();
    const matchedTab = WORKFORCE_PROFILE_TABS.find((tab) => tab.key === tabKey);
    setCurrentTab(matchedTab?.title || WORKFORCE_PROFILE_TABS[0].title);
  }, [window?.location.hash]);

  const handleEditSalary = () => setCurrentTab("edit-salary");

  const calculatedCostToCompany = costToCompany
    ? getFieldValue(costToCompany) - getFieldValue(bonus) - getFieldValue(incrementAmount)
    : "-";

  return (
    <>
      <Helmet>
        <title>{`Edge | Employees - ${getValue(firstName)} ${getValue(lastName)}`}</title>
      </Helmet>
      {(loadingGetEmployeeDetails || loadingEmployeeSalaryInfo) && <PageLoader />}
      <div
        className={`user-details-container ${platformHeader ? "p-0" : ""} `}
        style={{ rowGap: platformHeader ? "20px" : "24px" }}
      >
        <TopHeader
          title={
            currentTab !== "edit-salary"
              ? `${getValue(firstName)} ${getValue(lastName)}`
              : `Edit Salary for ${getMonthFromDate(salaryInfo?.payload?.paymentDate)}`
          }
          breadcrumbs={!platformHeader}
          primaryTitle="Workforce"
          primaryLink="/main/workforce"
          linkName="Profile"
          backIcon="/main/workforce"
          primaryBtn={showFinancials && currentTab !== "edit-salary" && "Edit Salary"}
          primaryBtnClick={handleEditSalary}
        />

        {currentTab === "edit-salary" ? (
          <EditSalary setTab={setCurrentTab} costToCompany={calculatedCostToCompany} />
        ) : (
          <div className="content-container">
            <div className="content-inner">
              <div>
                <ProfileStrip userData={getEmployeeDetails?.payload} image leaveStatus />
              </div>
              <div className="w-full">
                <ToolBar
                  tabs={(clockinCustomerExperience && clockinExperienceEnabled
                    ? WORKFORCE_PROFILE_TABS
                    : WORKFORCE_PROFILE_TABS.filter((tab) => tab.key !== "timesheet")
                  ).filter((tab) => showFinancials || tab.key !== "payroll-history")}
                  currentTab={currentTab}
                  setCurrentTab={setCurrentTab}
                />
                <div className="pt-15">
                  {currentTab === "Overview" && <ProfileTab />}
                  {currentTab === "Documents" && <DocumentsTab noPadding />}
                  {currentTab === "Payroll History" && showFinancials && <PayrollHistoryTab />}
                  {currentTab === "Education" && <EducationTab employeeId={employeeId} />}
                  {currentTab === "Experience" && <ExperienceTab employeeId={employeeId} />}
                  {currentTab === "Timesheet" && clockinCustomerExperience && clockinExperienceEnabled && (
                    <TimesheetTab />
                  )}
                  {currentTab === "Time Off" &&
                    (!timeOffAllowedCountries?.includes(anniversaryBasedTimeOff ? timeoffCountry : country) ||
                    !timeoffFlag ? (
                      <TimeoffNotAvailable />
                    ) : (
                      <TimeoffTab employeeCountry={country} />
                    ))}
                </div>
              </div>
            </div>
          </div>
        )}
      </div>
    </>
  );
}

export default UserDetails;
